import { Calendar, Pencil, Trash2, Paperclip } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useLongPress } from '@/shared/hooks/useLongPress';
import type { Plea } from '../types';
import { getPleaTypeStyle, getPleaStatusStyle, isPleaDecided } from '../constants';

interface PleaCardProps {
  plea: Plea;
  onEdit: (plea: Plea) => void;
  onDelete: (pleaId: string) => void;
  onView?: (plea: Plea) => void;
  onViewPeekEnd?: () => void;
}

function stopPropagation(e: React.SyntheticEvent) {
  e.stopPropagation();
}

function formatPleaDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

export function PleaCard({ plea, onEdit, onDelete, onView, onViewPeekEnd }: PleaCardProps) {
  const typeStyle = getPleaTypeStyle(plea.type);
  const statusStyle = getPleaStatusStyle(plea.status);
  const Icon = typeStyle.icon;
  const pleaId = plea.self.split('/').pop() ?? '';
  const decided = isPleaDecided(plea.status);

  const { firedRef, ...longPressHandlers } = useLongPress(() => onView?.(plea), {
    onRelease: onViewPeekEnd,
  });

  const handleClick = () => {
    if (firedRef.current) {
      firedRef.current = false;
      return;
    }
    onView?.(plea);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onView?.(plea);
  };

  return (
    <Card aria-label={`Pedido: ${plea.type}`}>
      <CardContent
        className="flex cursor-pointer select-none items-start gap-4"
        role="button"
        tabIndex={0}
        aria-label={`Ver detalhes do pedido: ${plea.type}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...longPressHandlers}
      >
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border',
            typeStyle.bg,
            typeStyle.border,
          )}
        >
          <Icon className={cn('h-5 w-5', typeStyle.color)} aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-start justify-between gap-2">
            <h4 className="truncate text-sm font-semibold text-slate-900">{plea.type}</h4>
            <span
              className={cn(
                'shrink-0 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                statusStyle.bg,
                statusStyle.text,
              )}
            >
              {statusStyle.label}
            </span>
          </div>

          <p className="mb-2 truncate text-xs text-slate-500">{plea.description}</p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-[10px] font-medium text-slate-400">
              <Calendar className="h-3 w-3" aria-hidden="true" />
              {formatPleaDate(plea.start)}
            </div>
            <div className="flex items-center gap-1" onClick={stopPropagation} onPointerDown={stopPropagation}>
              {(plea.attachmentsCount ?? 0) > 0 && (
                <span
                  className="mr-1 flex items-center gap-0.5 text-[10px] text-slate-400"
                  aria-label={`${plea.attachmentsCount} anexo(s)`}
                >
                  <Paperclip className="h-3 w-3" aria-hidden="true" />
                  {plea.attachmentsCount}
                </span>
              )}
              {!decided && (
                <>
                  <button
                    onClick={() => onEdit(plea)}
                    className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    aria-label="Editar pedido"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(pleaId)}
                    className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                    aria-label="Excluir pedido"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
